// Canvas display: render the emulator core's framebuffer to a <canvas>.
//
// Cores hand us a u32 framebuffer with layout 0x00BBGGRR (B high, R
// low — the canonical headless-core layout). The browser's ImageData
// stores RGBA as little-endian bytes [R, G, B, A], which as a u32 is
// 0xAABBGGRR. Going from core framebuffer to canvas is therefore a
// single `0xff000000 | pixel` per slot — one OR per pixel, in a tight
// Uint32Array loop.
//
// Dimensions are provided per session (256x240 for NES, 160x144 for
// GB). Calling setSize() rebuilds the backing ImageData; safe to call
// when the active core changes mid-page.
//
// Overscan crop: NES games render into 256x240 but the outer 8px ring is
// TV overscan (masked to black). We display only the visible interior
// (240x224) by wrapping the canvas in a clip box: the canvas keeps its
// full native backing store (so devtools' click-mapping via
// getBoundingClientRect and outline positioning via canvas.offsetLeft
// stay correct — they see the full geometry), but it's shifted up/left by
// the overscan and clipped to the visible size. GB has 0 overscan, so the
// clip box is the full frame.

export class CanvasDisplay {
  private canvas: HTMLCanvasElement;
  private crop: HTMLElement;
  private ctx: CanvasRenderingContext2D;
  private imageData!: ImageData;
  private rgba32!: Uint32Array;
  private width: number;
  private height: number;
  // Overscan ring in native pixels, cropped from the displayed area.
  private overscan = 0;
  // Displayed integer scale. The backing store is always native res; the
  // CSS pixel size is native * scale. The tile manager sets one scale for
  // the whole grid (so it fits the viewport) via setScale(); 2 is a
  // reasonable pre-rescale default.
  private scale = 2;

  constructor(canvas: HTMLCanvasElement, width: number = 256, height: number = 240) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) throw new Error("CanvasDisplay: failed to obtain 2d context");
    this.ctx = ctx;
    this.width = width;
    this.height = height;
    // Wrap the canvas in a clip box that crops the overscan. Inserted
    // where the canvas sat so the crop box is the tile's in-flow child.
    this.crop = document.createElement("div");
    this.crop.className = "screen-crop";
    canvas.parentElement?.insertBefore(this.crop, canvas);
    this.crop.appendChild(canvas);
    this.allocate();
  }

  // Displayed (visible, post-crop) dimensions — the tile manager reads
  // these to work out how many tiles fit at a given scale.
  get displayWidth(): number { return this.width - 2 * this.overscan; }
  get displayHeight(): number { return this.height - 2 * this.overscan; }

  // Set the displayed integer scale (CSS size = native * scale). Clamped
  // to >= 1; a no-op when unchanged so resize/relayout can call it freely.
  setScale(scale: number): void {
    const s = Math.max(1, Math.floor(scale));
    if (s === this.scale) return;
    this.scale = s;
    this.applyScale();
  }

  private applyScale(): void {
    const s = this.scale;
    const inset = this.overscan;
    // Canvas stays full native size (backing + CSS), just shifted so the
    // visible interior aligns to the clip box's top-left.
    this.canvas.style.width = `${this.width * s}px`;
    this.canvas.style.height = `${this.height * s}px`;
    this.canvas.style.marginLeft = `${-inset * s}px`;
    this.canvas.style.marginTop = `${-inset * s}px`;
    // Clip box shows only the visible interior.
    this.crop.style.width = `${this.displayWidth * s}px`;
    this.crop.style.height = `${this.displayHeight * s}px`;
  }

  // Resize the canvas + reallocate the ImageData, and set the overscan
  // crop. CSS sizing is re-applied for the new dimensions.
  setSize(width: number, height: number, overscan = 0): void {
    if (width === this.width && height === this.height && overscan === this.overscan) return;
    this.width = width;
    this.height = height;
    this.overscan = overscan;
    this.allocate();
  }

  private allocate(): void {
    this.canvas.width = this.width;
    this.canvas.height = this.height;
    // Re-apply the current scale to the new backing dimensions (e.g. when
    // the core changes from NES 256x240 to GB 160x144). The actual scale
    // value is chosen by the tile manager via setScale().
    this.applyScale();
    this.imageData = this.ctx.createImageData(this.width, this.height);
    // Share storage with imageData.data so we can write through a u32
    // view and have the canvas see the same bytes.
    this.rgba32 = new Uint32Array(this.imageData.data.buffer);
  }

  // Called once per emulator frame with the composited framebuffer.
  render(framebuffer: Uint32Array): void {
    const dst = this.rgba32;
    const src = framebuffer;
    for (let i = 0; i < src.length; i++) {
      dst[i] = (0xff000000 | src[i]!) >>> 0;
    }
    this.ctx.putImageData(this.imageData, 0, 0);
  }
}
